import { zodResolver } from '@hookform/resolvers/zod';
import type { PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import ProgressButton from '@/components/common/progress-button';
import { FieldMessage } from '@/components/fields/field-message';
import { OneTimeCodeField } from '@/components/fields/one-time-code-field';
import PasswordField from '@/components/fields/password-field';
import { PhoneField } from '@/components/fields/phone-field';
import { useVerifyOtp } from '@/hooks/auth/use-request-otp';

interface ForgotPasswordFormProps {
  isLoading: boolean;
  onSubmit: (data: ForgotPasswordFormData) => void;
}

export function ForgotPasswordForm({
  isLoading: isParentLoading,
  children,
  onSubmit,
}: PropsWithChildren<ForgotPasswordFormProps>) {
  const { t } = useTranslation();
  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordFromScheme),
    defaultValues: {
      countryCode: '86',
      phoneNumber: '',
      otpCode: '',
      password: '',
      confirmPassword: '',
    },
  });

  const { mutateAsync: verifyOtp, isVerified, isPending: isVerifyingOtp } = useVerifyOtp();

  const isLoading = isParentLoading || isVerifyingOtp;

  async function handleVerifyOtp() {
    if (await form.trigger(['countryCode', 'phoneNumber', 'otpCode'])) {
      const data = form.getValues();

      try {
        const res = await verifyOtp({ Phone: data.phoneNumber, TelCode: data.countryCode, Code: data.otpCode });

        form.clearErrors(['otpCode']);

        if (res.Code !== 0) {
          form.setError('otpCode', { message: res.Msg });
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  const handleResetPassword = form.handleSubmit((d) => {
    if (isVerified) {
      onSubmit(d);
    }
  }, console.error);

  return (
    <div className='flex size-full flex-1 flex-col'>
      <h2 className='mb-8 text-center text-2xl font-bold text-primary-content'>
        {isVerified ? t('auth.reset-password') : t('auth.forgot-password')}
      </h2>
      <FormProvider {...form}>
        <form
          className='flex flex-1 flex-col'
          onSubmit={handleResetPassword}>
          <div className='form-control w-full'>
            <PhoneField
              names={['countryCode', 'phoneNumber']}
              placeholder={t('auth.phone-number')}
              control={form.control}
              disabled={isVerified}
            />

            <FieldMessage
              name='phoneNumber'
              errors={form.formState.errors}
            />
          </div>

          {!isVerified && (
            <div className='form-control mt-4 w-full'>
              <OneTimeCodeField
                id='otpCode'
                name='otpCode'
                disabled={isVerified}
                placeholder={t('auth.one-time-password')}
                type='sign-in'
              />

              <FieldMessage
                name='otpCode'
                errors={form.formState.errors}
              />
            </div>
          )}

          {isVerified && (
            <>
              <div className='form-control mt-4 w-full'>
                <PasswordField
                  placeholder={t('auth.enter-new-password')}
                  id='password'
                  {...form.register('password')}
                />

                <FieldMessage
                  name='password'
                  errors={form.formState.errors}
                />
              </div>

              <div className='form-control mt-4 w-full'>
                <PasswordField
                  placeholder={t('auth.re-enter-new-password')}
                  id='confirmPassword'
                  {...form.register('confirmPassword')}
                />

                <FieldMessage
                  name='confirmPassword'
                  errors={form.formState.errors}
                />
              </div>
            </>
          )}

          <div className='flex-1' />

          <div className='form-control flex gap-4 flex-row mt-8'>
            {children}

            {isVerified ? (
              <ProgressButton
                as='submit'
                isLoading={isLoading}
                label='common.done'
                className='flex-1 btn btn-revert-primary'
              />
            ) : (
              <ProgressButton
                as='button'
                isLoading={isLoading}
                className='flex-1 btn btn-revert-primary'
                onClick={handleVerifyOtp}
                label='auth.next-step'
              />
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

const forgotPasswordFromScheme = z
  .object({
    countryCode: z.string(),
    phoneNumber: z.string().min(8, 'errors.string.length|count=8').regex(/^\d+$/),
    otpCode: z.string().min(6, 'errors.string.length|count=6'),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'errors.password-not-matched',
    path: ['confirmPassword'],
  });

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordFromScheme>;
